import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadNew from "@/pages/LeadNew";
import LeadNewAI from "@/pages/LeadNewAI";
import LeadDetail from "@/pages/LeadDetail";
import Offers from "@/pages/Offers";
import OfferEditor from "@/pages/OfferEditor";
import Followups from "@/pages/Followups";
import Objections from "@/pages/Objections";
import Reporting from "@/pages/Reporting";
import Settings from "@/pages/Settings";
import PublicOffer from "@/pages/PublicOffer";
import AgencyClients from "@/pages/AgencyClients";
import AgencyDashboard from "@/pages/AgencyDashboard";
import AgencyOnboarding from "@/pages/AgencyOnboarding";
import AgencyLanding from "@/pages/AgencyLanding";
import AgencyPortal from "@/pages/AgencyPortal";
import ClientOnboarding from "@/pages/ClientOnboarding";
import ClientUsers from "@/pages/ClientUsers";
import InviteAccept from "@/pages/InviteAccept";
import Imprint from "@/pages/legal/Imprint";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Cookies from "@/pages/legal/Cookies";
import CookieBanner from "@/components/CookieBanner";
import { HostnameProvider, useHostname } from "@/components/HostnameProvider";
import UnknownDomainPage from "@/pages/UnknownDomainPage";
import Layout from "@/components/Layout";
import ConfiguratorAdmin from "@/pages/ConfiguratorAdmin";
import EmbedConfigurator from "@/pages/EmbedConfigurator";
import ConversionQueue from "@/pages/ConversionQueue";
import PlatformAdminAgencies from "@/pages/PlatformAdminAgencies";
import "@/index.css";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-sm">Wird geladen …</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Sprint 8a — Company-Offer-Domain: minimaler Router (nur Public Offer + Legal-Public)
function CompanyOfferOnlyRouter() {
  return (
    <Routes>
      <Route path="/public/offer/:id" element={<PublicOffer />} />
      <Route path="/impressum" element={<Imprint />} />
      <Route path="/datenschutz" element={<Privacy />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="*" element={<UnknownDomainPage />} />
    </Routes>
  );
}

function AppRouter() {
  const location = useLocation();
  const host = useHostname();
  // Synchronously detect OAuth callback BEFORE other routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Sprint 8a — Hostname-Modi
  if (host.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-sm">Wird geladen …</div>
      </div>
    );
  }
  if (host.type === "company_offer") {
    return <CompanyOfferOnlyRouter />;
  }
  if (host.type === "unknown") {
    return <UnknownDomainPage />;
  }
  // agency_portal & default fallen weiterhin in den vollen Router —
  // agency_portal redirected die Root-Route auf /portal/:slug, sonst bleibt alles wie default.
  if (host.type === "agency_portal" && host.agency?.agency_slug && location.pathname === "/") {
    return <Navigate to={`/portal/${host.agency.agency_slug}`} replace />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/public/offer/:id" element={<PublicOffer />} />
      <Route path="/agentur" element={<AgencyLanding />} />
      <Route path="/portal/:agency_slug" element={<AgencyPortal />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/embed/configurator/:id" element={<EmbedConfigurator />} />
      <Route path="/impressum" element={<Imprint />} />
      <Route path="/datenschutz" element={<Privacy />} />
      <Route path="/agb" element={<Terms />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/new" element={<LeadNew />} />
        <Route path="/leads/new-ai" element={<LeadNewAI />} />
        <Route path="/leads/:id" element={<LeadDetail />} />
        <Route path="/offers" element={<Offers />} />
        <Route path="/offers/:id/edit" element={<OfferEditor />} />
        <Route path="/followups" element={<Followups />} />
        <Route path="/queue" element={<ConversionQueue />} />
        <Route path="/admin/agencies" element={<PlatformAdminAgencies />} />
        <Route path="/objections" element={<Objections />} />
        <Route path="/reporting" element={<Reporting />} />
        <Route path="/agency/dashboard" element={<AgencyDashboard />} />
        <Route path="/agency/onboarding" element={<AgencyOnboarding />} />
        <Route path="/agency/clients" element={<AgencyClients />} />
        <Route path="/agency/clients/:client_id/users" element={<ClientUsers />} />
        <Route path="/onboarding/client" element={<ClientOnboarding />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/configurator" element={<ConfiguratorAdmin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <HostnameProvider>
        <AuthProvider>
          <AppRouter />
          <CookieBanner />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </HostnameProvider>
    </BrowserRouter>
  );
}
